import { toast } from "react-toastify";
import moment from "moment";
import { createContext } from "react";
import CryptoJS from "crypto-js";
import { jsPDF } from "jspdf";
import { ReactComponent as PdfIcon } from "../images/pdf.svg";
import { ReactComponent as DocIcon } from "../images/docs.svg";
import { ReactComponent as ExcelIcon } from "../images/excel.svg";
import { ReactComponent as ImageIcon } from "../images/image.svg";
import { ReactComponent as WordIcon } from "../images/word.svg";
import FolderIcon from "@mui/icons-material/Folder";
import { font } from "../resources/fonts/fontBase64";
import ValidationLibrary from "./validationfunction";
import {
  formatInDisplayTimeZone,
  formatTimeInDisplayZone,
} from "./workCalendar";
import {
  getAuthRole,
  getAuthUserTypeId,
} from "./authSession";

export const dropdownNameWithEmail = (data) => {
  return `${data?.first_name} ${data?.last_name} - ${data?.email}`;
};
export const dropdownName = (data) => {
  return `${data?.first_name} ${data?.last_name}`;
};
export const NotificationContext = createContext({
  notifications: [],
  setNotifications: () => {},
  notificationMeta: {
    page: 0,
    hasMore: true,
    unreadCount: null,
    total: null,
    loading: false,
    loadingMore: false,
  },
  setNotificationMeta: () => {},
});

export const Toaster = {
  warn: function (value) {
    toast.dismiss();
    toast.warn(value);
  },
  error: function (value) {
    toast.clearWaitingQueue("1");
    toast.dismiss();
    // if value is html content
    if (value?.includes("<div>")) {
      toast.error(<div dangerouslySetInnerHTML={{ __html: value }} />);
    }
    //toast.dismiss();
    else
      toast.error(value, {
        toastId: "1",
      });
    //toast.dismiss();
  },
  success: function (value) {
    toast.dismiss();
    toast.success(value);
  },
  info: function (value) {
    toast.dismiss();
    toast.info(value);
  },
  warning: function (value) {
    toast.dismiss();
    toast.warn(value);
  },
  remove: function () {
    toast.dismiss();
  },
};
export const dateFormatter = (date, current) => {
  if (current) {
    return formatInDisplayTimeZone(new Date(), { includeTime: false });
  }

  if (!date || date === "") return "";
  const s = String(date).trim();
  // Date-only calendar values stay timezone-neutral (no day shift).
  if (/^\d{4}-\d{2}-\d{2}/.test(s) && !/[T\s]\d{1,2}:\d{2}/.test(s)) {
    return moment(s.slice(0, 10), "YYYY-MM-DD", true).format("MMM DD yyyy");
  }
  return formatInDisplayTimeZone(date, { includeTime: false });
};

export const dateTimeFormatter = (date) => {
  if (!date || date === "") return "";
  const s = String(date).trim();
  // Pure YYYY-MM-DD has no time — show calendar day only (no TZ shift).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return moment(s, "YYYY-MM-DD", true).format("MMM DD yyyy");
  }
  return formatInDisplayTimeZone(date, { includeTime: true });
};

/** HH:mm in the viewer's US/India display timezone. */
export const timeFormatter = (date) => formatTimeInDisplayZone(date);

/** Normalize to 24-hour HH:mm (e.g. Excel "1:00" → "01:00"). */
export function normalizeTimeHHmm(value) {
  if (value == null || value === "") return "";
  if (moment.isMoment(value)) {
    return value.isValid() ? value.format("HH:mm") : "";
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return moment(value).format("HH:mm");
  }
  const s = String(value).trim();
  if (!s || /invalid/i.test(s)) return "";
  const parsed = moment(s, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"], true);
  if (parsed.isValid()) return parsed.format("HH:mm");
  const match = s.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (match) {
    const h = Number(match[1]);
    const m = Number(match[2]);
    if (h >= 0 && h < 24 && m >= 0 && m < 60) {
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }
  }
  return "";
}

export function parseTimeToMoment(value) {
  const normalized = normalizeTimeHHmm(value);
  if (!normalized) return null;
  const m = moment(normalized, "HH:mm", true);
  return m.isValid() ? m : null;
}

/** e.g. 0.50 → 0:50; not used for Excel serials like 0.04167 (1:00). */
function isPlausibleHoursDotMinutes(num) {
  if (num < 0 || num >= 24) return false;
  const h = Math.floor(num);
  const fractional = num - h;
  if (fractional === 0) return Number.isInteger(num) || Math.abs(num - h) < 1e-9;
  const minutes = Math.round(fractional * 100);
  if (minutes < 0 || minutes >= 60) return false;
  const roundedCent = Math.round(num * 100) / 100;
  return Math.abs(num - roundedCent) < 1e-8;
}

function durationFromExcelSerialFraction(num) {
  const totalMinutes = Math.round(num * 24 * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return normalizeTimeHHmm(`${h}:${m}`);
}

/** Parse Hours column from Excel (serial, Date, or "1:00" text). */
export function parseHoursFromExcel(val) {
  if (val == null || val === "") return "";

  if (typeof val === "number" && !Number.isNaN(val)) {
    const num = val;

    // Excel time serial (fraction of a day), e.g. 1:00 → ~0.04167
    if (num > 0 && num < 1 && !isPlausibleHoursDotMinutes(num)) {
      const fromSerial = durationFromExcelSerialFraction(num);
      if (fromSerial) return fromSerial;
    }

    // Manual HH.MM entry (0.50 = 0:50, 1.30 = 1:30)
    if (num >= 0 && num < 24 && isPlausibleHoursDotMinutes(num)) {
      const h = Math.floor(num);
      const minutes = Math.round((num - h) * 100);
      return normalizeTimeHHmm(`${h}:${minutes}`);
    }

    if (num >= 0 && num < 1) {
      const fromSerial = durationFromExcelSerialFraction(num);
      if (fromSerial) return fromSerial;
    }

    if (Number.isInteger(num) && num >= 0 && num < 24) {
      return normalizeTimeHHmm(`${num}:00`);
    }
  }

  if (val instanceof Date && !Number.isNaN(val.getTime())) {
    // SheetJS cellDates uses local time — do not use UTC (causes ~timezone offset).
    const h = val.getHours();
    const m = val.getMinutes();
    return normalizeTimeHHmm(`${h}:${m}`);
  }

  const s = String(val).trim();
  if (!s) return "";

  // Support Excel inputs like "0.50" meaning 0:50 (HH:MM), not a fraction.
  // i.e. left side hours, right side minutes (00-59).
  const dotTime = s.match(/^(\d{1,2})[.,](\d{2})$/);
  if (dotTime) {
    const h = Number(dotTime[1]);
    const m = Number(dotTime[2]);
    if (
      !Number.isNaN(h) &&
      !Number.isNaN(m) &&
      h >= 0 &&
      h < 24 &&
      m >= 0 &&
      m < 60
    ) {
      return normalizeTimeHHmm(`${h}:${m}`);
    }
  }

  if (/^\d{1,2}:\d{1,2}/.test(s)) {
    return normalizeTimeHHmm(s);
  }

  const parsed = moment(
    s,
    ["HH:mm:ss", "H:mm:ss", "HH:mm", "H:mm", "h:mm:ss A", "h:mm A"],
    true,
  );
  if (parsed.isValid()) {
    return parsed.format("HH:mm");
  }

  const loose = moment(s, ["HH:mm:ss", "H:mm:ss", "HH:mm", "H:mm"]);
  if (loose.isValid()) {
    return loose.format("HH:mm");
  }

  const num = parseFloat(s);
  if (!Number.isNaN(num)) {
    if (num >= 0 && num < 1) {
      const totalMinutes = Math.round(num * 24 * 60);
      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      return normalizeTimeHHmm(`${h}:${m}`);
    }
    if (Number.isInteger(num) && num >= 0 && num < 24) {
      return normalizeTimeHHmm(`${num}:00`);
    }
  }

  return normalizeTimeHHmm(s);
}

// export const dateTimeFormatter = (date) => {
//   return date && date !== "" ? moment(date).format("MMM DD YYYY hh:mm A") : "";
// };

export const encryptPassword = (password) => {
  return CryptoJS.AES.encrypt(password, "finlotax").toString();
};

/** Generates a random password meeting signup complexity rules. */
export const generateSecurePassword = (length = 12) => {
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const digits = "0123456789";
  const special = "!@#$%&*";
  const all = lowercase + uppercase + digits + special;

  const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
  const required = [pick(lowercase), pick(uppercase), pick(digits), pick(special)];
  const remaining = Array.from({ length: Math.max(length - required.length, 0) }, () =>
    pick(all),
  );
  const chars = [...required, ...remaining];

  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join("");
};

export const decryptPassword = (password) => {
  if (password === null || password === undefined || password === "") return "";
  try {
    const bytes = CryptoJS.AES.decrypt(String(password), "finlotax");
    return bytes.toString(CryptoJS.enc.Utf8) || "";
  } catch (_e) {
    return "";
  }
};

export const getNodeText = (node, joinValue) => {
  if (["string", "number"].includes(typeof node)) return node;
  if (node instanceof Array) return node.map(getNodeText).join(joinValue || "");
  if (typeof node === "object" && node) return getNodeText(node.props.children);
};

export const Loader = (isShow) => {
  // eslint-disable-next-line no-unused-expressions
  isShow
    ? document.getElementById("showLoader") !== null
      ? document.getElementById("showLoader").click()
      : false
    : document.getElementById("hideLoader") !== null
      ? document.getElementById("hideLoader").click()
      : false;
};

export function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export const detailsBind = (keys, values, Arr) => {
  let details = {};
  keys.forEach((data, index) => {
    details[data] = {
      value: values[index] === 0 || values[index] ? Arr[values[index]] : "",
    };
  });
  return details;
};
export const addressFormator = (...value) => {
  const inputValues = [...value];
  const validValues = inputValues
    .map((data) =>
      data === null || data === undefined ? "" : String(data).trim(),
    )
    .filter((data) => data && data !== "null" && data !== "-");

  return validValues.join(", ");
};

export const getUserTypeID = () => getAuthUserTypeId();

// Get role-based path prefix (e.g., "/finlo/admin", "/finlo/employee")
export const getRolePath = (path = "") => {
  const user_role = getAuthRole();
  const rolePrefix = `/finlo/${String(user_role).toLowerCase()}`;
  return path ? `${rolePrefix}${path}` : rolePrefix;
};

// Get role from authenticated session (JWT /auth/me), not editable localStorage alone
export const getUserRole = () => getAuthRole();

function applyCancelledInvoiceWatermarkToPdf(pdfDoc) {
  const pageCount = pdfDoc.internal.getNumberOfPages();
  const pageWidth = pdfDoc.internal.pageSize.getWidth();
  const pageHeight = pdfDoc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;
  const centerY = pageHeight / 2;

  for (let page = 1; page <= pageCount; page += 1) {
    pdfDoc.setPage(page);
    pdfDoc.saveGraphicsState();
    if (typeof pdfDoc.GState === "function") {
      pdfDoc.setGState(new pdfDoc.GState({ opacity: 0.2 }));
    }
    pdfDoc.setFont("helvetica", "bold");
    pdfDoc.setFontSize(52);
    pdfDoc.setTextColor(211, 47, 47);
    pdfDoc.text("CANCELLED", centerX, centerY, {
      align: "center",
      baseline: "middle",
      angle: 35,
    });
    pdfDoc.restoreGraphicsState();
  }
}

export const getDocumentBlob = async () => {
  const input = document.getElementById("divToPrint");
  if (!input) {
    return Promise.reject(
      new Error("Invoice document is not ready. Please try again."),
    );
  }

  const isCancelledInvoice = input.classList.contains(
    "invoice-card-wrapper--cancelled",
  );

  return new Promise((resolve, reject) => {
    const framebox = document.getElementsByClassName("silver-fieldset");
    for (var i = 0; i < framebox.length; i++) {
      framebox[i].classList.add("pdf-framebox");
    }
    const doc = new jsPDF({ unit: "px", format: "letter", userUnit: "px" });
    let docWidth = doc.internal.pageSize.getWidth();

    doc.addFileToVFS("Nunito-Regular.ttf", font);
    doc.addFont("Nunito-Regular.ttf", "Nunito Regular", "normal");
    doc.setFont("Nunito Regular");
    doc
      .html(input, {
        callback: function (pdfDoc) {
          if (isCancelledInvoice) {
            applyCancelledInvoiceWatermarkToPdf(pdfDoc);
          }
          resolve(pdfDoc.output("blob"));

          // doc.save("file.pdf");
        },

        autoPaging: "text",
        margin: [0, 0, 0, 0],
        width: docWidth,
        windowWidth: 1000,
      })
      .then(() => {
        for (var i = 0; i < framebox.length; i++) {
          framebox[i].classList.remove("pdf-framebox");
        }
      })
      .catch((err) => {
        for (var j = 0; j < framebox.length; j++) {
          framebox[j].classList.remove("pdf-framebox");
        }
        reject(err);
      });
  });
};

export function checkValidation({ key, value, state, setState }) {
  const errorcheck = ValidationLibrary.checkValidation(
    value,
    state[key].validation,
  );
  const dynObj = {
    ...state[key],
    value,
    error: !errorcheck.state,
    errmsg: errorcheck.msg,
  };

  setState((prevState) => ({
    ...prevState,
    [key]: dynObj,
  }));
}
export function cancelValidation({ setState, defaultState }) {
  setState(defaultState);
}
export function onSubmitValidation({ state, setState, checkFields }) {
  let filtererr = [];
  const fields = checkFields?.length > 0 ? checkFields : state;
  for (const keys in fields) {
    const key = checkFields?.length > 0 ? fields[keys] : keys;
    const errorcheck = ValidationLibrary.checkValidation(
      state[key].value,
      state[key].validation,
    );
    state[key].error = !errorcheck.state;
    state[key].errmsg = errorcheck.msg;
    if (state[key].error) filtererr.push(key);
  }
  setState((prevState) => ({
    ...prevState,
  }));
  return filtererr;
}
export const getReadableFileType = (mimeType, isFolder = false) => {
  if (isFolder) return "Folder";

  if (!mimeType || typeof mimeType !== "string") return "-";

  if (mimeType.startsWith("image/")) return "Image";

  if (mimeType === "application/pdf") return "PDF";

  if (
    mimeType === "application/msword" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "Word";
  }

  if (
    mimeType === "application/vnd.ms-excel" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return "Excel";
  }

  return "File";
};

export const getFileTypeIcon = (mimeType, isFolder = false) => {
  if (isFolder)
    return <FolderIcon className="file-type-icon folder-type-icon" />;

  // ✅ SAFETY GUARD
  if (!mimeType || typeof mimeType !== "string") {
    return <DocIcon className="file-type-icon" />;
  }

  // Image
  if (mimeType.startsWith("image/")) {
    return <ImageIcon className="file-type-icon" />;
  }

  // PDF
  if (mimeType === "application/pdf") {
    return <PdfIcon className="file-type-icon" />;
  }

  // Word
  if (
    mimeType === "application/msword" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return <WordIcon className="file-type-icon" />;
  }

  // Excel
  if (
    mimeType === "application/vnd.ms-excel" ||
    mimeType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return <ExcelIcon className="file-type-icon" />;
  }

  return <DocIcon className="file-type-icon" />;
};
